function __match(v,ex){if(ex===null)return v===null;if(ex===undefined)return true;const t=typeof ex;if(t==='string'||t==='number'||t==='boolean')return typeof v===t;if(Array.isArray(ex)){if(!Array.isArray(v))return false;return ex.length?v.every(x=>__match(x,ex[0])):true}if(t==='object'){if(!v||typeof v!=='object'||Array.isArray(v))return false;return Object.keys(ex).every(k=>k in v&&__match(v[k],ex[k]))}return v===ex};
function Type(d,p,e){const t={description:d,__runtimeType:true};if(typeof p==='function'){t.check=p;t.default=e??null}else{const ex=e??p;t.default=ex;t.__ex=ex;t.check=v=>__match(v,ex)}return t};
const __tjs = globalThis.__tjs?.createRuntime?.() ?? {Type};
/* tjs <- share.ts */

/*#
# share

`share()` synchronizes state across browser tabs and windows. Pass it
boxed proxies from `tosi()` and those paths will be kept in sync via
`BroadcastChannel` and persisted to `IndexedDB`.

```
import { tosi, share } from 'tosijs'

const { app } = tosi({
  app: { user: null, settings: { theme: 'light' } }
})

const { restored } = await share(app.user, app.settings)

if (restored.length > 0) {
  // another tab had already stored state — we inherited it
}
```

> `restored` contains the arguments whose values were overwritten from the
> store. Compare by **path**, not by proxy identity — boxed proxies are
> created per access, so `restored.includes(app.user)` is `false` even when
> `app.user` *was* restored. Use `share('app.user')` with string paths, or
> `restored.map(tosiPath)`, if you need to know *which* ones.

The first tab to call `share()` seeds the store. Subsequent tabs
inherit that data, overwriting their `tosi()` defaults. After setup,
changes in any tab propagate to all others in real-time.

## What to share

Share **small, session-level state**: user identity, preferences,
auth tokens, UI mode, active selections, cache-invalidation keys.

Don't share large datasets directly. Instead, share query metadata
(URLs, cache keys, timestamps) and let each tab fetch or cache the
data independently. This keeps the sync layer fast and avoids
hitting `BroadcastChannel` or `IndexedDB` with multi-megabyte writes.

## How it works

- Changes are sent as **deltas** via `BroadcastChannel` — only the
  changed path and its value are transmitted, not the entire root
  object.
- **Persistence** uses `IndexedDB` (async, no size limit). Full
  root-path snapshots are written on a debounced schedule for
  cold-start recovery.

## API

    share(...proxiesOrPaths: (BoxedProxy | string)[]): Promise<{ restored: (BoxedProxy | string)[] }>

- Accepts boxed proxies (from `tosi()`) or string paths
- Returns `{ restored }` — the subset of arguments whose values were
  overwritten from pre-existing stored data
- Idempotent: sharing the same path twice is a no-op
- Throws if an argument is neither a proxy nor a string

To clear shared state (e.g. on logout), set the values to their
empty/default state. The change will propagate to all tabs and
persist.

## Live Demo

Drag the squares around, then click **New Window** to open a second
copy. Drag in either window and watch the other update in real-time.

```html
<div class="draggable" data-key="red"></div>
<div class="draggable" data-key="green"></div>
<div class="draggable" data-key="blue"></div>
<button class="spawn">New Window</button>
```
```css
.preview {
  touch-action: none;
  min-height: 200px;
}

.draggable {
  position: absolute;
  width: 50px;
  height: 50px;
  cursor: move;
  border-radius: 6px;
}

.draggable[data-key="red"]   { background: #f008; }
.draggable[data-key="green"] { background: #0f08; }
.draggable[data-key="blue"]  { background: #00f8; }

.spawn {
  position: absolute;
  bottom: 8px;
  right: 8px;
}
```
```js
import { tosi, share, xin } from 'tosijs'
import { trackDrag } from 'tosijs-ui'

const { squares } = tosi({
  squares: {
    red:   { x: 20,  y: 20 },
    green: { x: 120, y: 20 },
    blue:  { x: 220, y: 20 },
  }
})

await share(squares)

const draggables = [...preview.querySelectorAll('.draggable')]

function render() {
  for (const el of draggables) {
    const key = el.dataset.key
    el.style.left = xin.squares[key].x + 'px'
    el.style.top = xin.squares[key].y + 'px'
  }
}

render()
squares.observe(render)

function dragItem(event) {
  const el = event.target.closest('.draggable')
  if (!el) return
  const key = el.dataset.key
  const start = { ...xin.squares[key] }
  trackDrag(event, (dx, dy, event) => {
    xin.squares[key] = { x: start.x + dx, y: start.y + dy }
    render()
    return event.type === 'mouseup'
  })
}

preview.addEventListener('mousedown', dragItem)
preview.addEventListener('touchstart', dragItem, { passive: true })

preview.querySelector('.spawn').addEventListener('click', () => {
  window.open(location.href)
})
```
*/

import { registry } from './registry'

import { getByPath, setByPath } from './by-path'

import { touch, observe, updates } from './path-listener'

import { tosiPath } from './metadata'

import { debounce } from './throttle'

const CHANNEL_NAME = 'tosijs-share';

const DB_NAME = 'tosijs-share';

const STORE_NAME = 'shared';

const DB_VERSION = 1;

const ShareMessage = Type('ShareMessage', undefined, { type: 'tosijs-share', path: '', value: null, origin: '' })

const sharedPaths = new Set();

const inboundPaths = new Set();

const restoringPaths = new Set();

const freshDuringRestore = new Set();

const debouncedSaves = new Map();

let channel = null;

let origin = '';

let db = null;

export const ShareStore = Type('ShareStore')

let store = null;

/* line 193 */
export function setShareStore(s) {
    store = s;
}
setShareStore.__tjs = {
  "params": {
    "s": {
      "type": {
        "kind": "object",
        "shape": {}
      },
      "required": true,
      "default": null
    }
  },
  "unsafe": true,
  "source": "share.ts:191"
}

/* line 199 */
/* TODO: TS types degraded — return: Promise<IDBDatabase> */
function openDB() {
    if (db != null)
        return Promise.resolve(db);
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            req.result.createObjectStore(STORE_NAME);
        };
        req.onsuccess = () => {
            db = req.result;
            resolve(db);
        };
        req.onerror = () => reject(req.error);
    });
}
openDB.__tjs = {
  "params": {},
  "unsafe": true,
  "source": "share.ts:197"
}

const idbStore = {
    async get(key) {
        const database = await openDB();
        return new Promise((resolve, reject) => {
            const tx = database.transaction(STORE_NAME, 'readonly');
            const req = tx.objectStore(STORE_NAME).get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    },
    async set(key, value) {
        const database = await openDB();
        return new Promise((resolve, reject) => {
            const tx = database.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    },
};

/* line 235 */
function getStore() {
    return store ?? idbStore;
}
getStore.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "share.ts:235"
}

/* line 241 */
/* TODO: TS types degraded — return: data is ShareMessage */
function isShareMessage(data) {
    return (data != null &&
        data.type === 'tosijs-share' &&
        typeof data.path === 'string');
}
isShareMessage.__tjs = {
  "params": {
    "data": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "unsafe": true,
  "source": "share.ts:241"
}

/* line 249 */
function findSharedRoot(changedPath) {
    for (const shared of sharedPaths) {
        if (changedPath === shared || changedPath.startsWith(shared + '.')) {
            return shared;
        }
    }
    return undefined;
}
findSharedRoot.__tjs = {
  "params": {
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "returns": {
    "type": {
      "kind": "union",
      "members": [
        {
          "kind": "string"
        },
        {
          "kind": "undefined"
        }
      ]
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "share.ts:249"
}

/* line 258 */
function isInbound(changedPath) {
    for (const path of inboundPaths) {
        if (changedPath === path || changedPath.startsWith(path + '.')) {
            return true;
        }
    }
    return false;
}
isInbound.__tjs = {
  "params": {
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "returns": {
    "type": {
      "kind": "boolean"
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "share.ts:258"
}

/* line 267 */
function applyInbound(path, value) {

    const root = findSharedRoot(path);
    if (root !== undefined && restoringPaths.has(root)) {
        freshDuringRestore.add(root);
    }
    inboundPaths.add(path);
    setByPath(registry, path, value);
    touch(path);
    updates().then(() => {
        inboundPaths.delete(path);
    });
}
applyInbound.__tjs = {
  "params": {
    "path": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "value": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "unsafe": true,
  "source": "share.ts:267"
}

/* line 282 */
function initChannel() {
    if (channel != null)
        return channel;
    origin = crypto.randomUUID();
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
        const msg = event.data;
        if (!isShareMessage(msg))
            return;
        if (msg.origin === origin)
            return;
                                                                
        if (findSharedRoot(msg.path) === undefined)
            return;
        applyInbound(msg.path, msg.value);
    };
    return channel;
}
initChannel.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "share.ts:282"
}

/* line 301 */
function broadcast(path, value) {
    if (channel == null)
        return;
    const msg = {
        type: 'tosijs-share',
        path,
        value,
        origin,
    };
    channel.postMessage(msg);
}
broadcast.__tjs = {
  "params": {
    "path": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "value": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "unsafe": true,
  "source": "share.ts:301"
}

/* line 312 */
function schedulePersist(rootPath) {
    if (!debouncedSaves.has(rootPath)) {
        debouncedSaves.set(rootPath, debounce(() => {
            const current = getByPath(registry, rootPath);
            getStore().set(rootPath, current);
        }, 500));
    }
    debouncedSaves.get(rootPath)();
}
schedulePersist.__tjs = {
  "params": {
    "rootPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "unsafe": true,
  "source": "share.ts:312"
}

/* line 325 */
export async function share(...proxies) {
                                        
    if (typeof BroadcastChannel === 'undefined') {
        return { restored: [] };
    }
    initChannel();
    const restored = [];
    const s = getStore();
    for (const proxy of proxies) {
        const path = typeof proxy === 'string' ? proxy : tosiPath(proxy);
        if (path === undefined) {
            throw new Error('share() requires boxed proxies or string paths. Got a non-proxy value.');
        }
                                              
        if (sharedPaths.has(path))
            continue;

        sharedPaths.add(path);
        restoringPaths.add(path);
                                        
        const stored = await s.get(path);
        const gotFresh = freshDuringRestore.has(path);
        restoringPaths.delete(path);
        freshDuringRestore.delete(path);
        if (stored !== undefined && !gotFresh) {

            applyInbound(path, stored);
            restored.push(proxy);
        }
        else if (stored === undefined) {
            const value = getByPath(registry, path);
            await s.set(path, value);
        }
                                                              
        observe((changedPath) => changedPath === path || changedPath.startsWith(path + '.'), (changedPath) => {
            if (isInbound(changedPath))
                return;
            const rootPath = findSharedRoot(changedPath);
            if (rootPath === undefined)
                return;
                                                      
            const value = getByPath(registry, changedPath);
            broadcast(changedPath, value);
                                                              
            schedulePersist(rootPath);
        });
    }
    return { restored };
}
share.__tjs = {
  "params": {
    "proxies": {
      "type": {
        "kind": "array",
        "items": {
          "kind": "null"
        }
      },
      "required": false
    }
  },
  "returns": {
    "type": {
      "kind": "object",
      "shape": {
        "restored": {
          "kind": "array",
          "items": {
            "kind": "null"
          }
        }
      }
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "share.ts:327"
}